const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function handle(res: Response) {
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      detail = data.detail || detail;
    } catch {
      /* ignore */
    }
    throw new Error(typeof detail === 'string' ? detail : 'Erreur serveur');
  }
  return res.json();
}

export const iotApi = {
  get: (path: string) => fetch(`${BASE}/api${path}`, { headers: authHeaders() }).then(handle),
  post: (path: string, body?: unknown) =>
    fetch(`${BASE}/api${path}`, {
      method: 'POST',
      headers: authHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    }).then(handle),
  put: (path: string, body?: unknown) =>
    fetch(`${BASE}/api${path}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    }).then(handle),
  del: (path: string) => fetch(`${BASE}/api${path}`, { method: 'DELETE', headers: authHeaders() }).then(handle),
  // Authenticated file download (CSV/PDF) — fetch as blob then trigger save.
  download: async (path: string, fallbackName: string) => {
    const res = await fetch(`${BASE}/api${path}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename="?([^"]+)"?/);
    const name = match ? match[1] : fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// ---- Types ----
export interface NodeLatest {
  measures?: {
    battery_v?: number;
    valve_state?: number;
    flow_velocity_lpm?: number;
    flow_rate_l?: number;
    valve_on_seconds?: number;
    reporting_interval_s?: number;
    packet_seq?: number;
  };
  ts?: string;
}

export interface IoTNode {
  id: string;
  name: string;
  model: string;
  dev_eui: string;
  ttn_device_id?: string;
  valve_state?: number | null;
  battery_v?: number | null;
  last_seen_at?: string | null;
  latest?: NodeLatest | null;
  sector_id?: string | null;
  sector_name?: string | null;
}

export interface Command {
  id: string;
  node_id: string;
  type: string;
  params: Record<string, unknown>;
  f_port: number;
  payload_hex: string;
  status: string;
  requested_by: string;
  requested_at: string;
  sent_at?: string | null;
  observed_at?: string | null;
}

export interface IoTAlert {
  id: string;
  kind: string;
  severity: string;
  message: string;
  status: string;
  node_name?: string;
  created_at: string;
}

export interface DashboardStats {
  nodes_total: number;
  nodes_online: number;
  valves_open: number;
  open_alerts: number;
  commands_total: number;
  commands_success: number;
}

export interface TelemetrySummary {
  sample_count: number;
  total_volume_l: number;
  avg_flow_lpm: number;
  max_flow_lpm: number;
  valve_open_minutes: number;
  battery_min_v: number | null;
  battery_max_v: number | null;
  window_hours?: number;
}
